'use client';

import { Check } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import { HeidiProFlashFilledSvg } from '@/assets/image/free-to-pay/heidi-pro-flash-filled-svg';
import {
  PopoverDrawer,
  PopoverDrawerContent,
  PopoverDrawerTrigger,
} from '@/components/ui/monbile-responsive-popover-drawer';
import type { ComboBoxItemType } from '@/components/ui/select/combobox';
import { useIsFreeUser } from '@/hooks/useIsFreeUser';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { ComboboxTrigger } from '@heidi/design-system/combobox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@heidi/design-system/command';
import { DrawerHeader, DrawerTitle } from '@heidi/design-system/drawer';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { TypographyP2 } from '@heidi/design-system/typography';

import { personalizationSharedMessages } from '../personalization-shared.messages';

type OnboardingComboboxProps = {
  value?: string;
  onSelect: (value: string | undefined) => void;
  items: ComboBoxItemType[];
  searchPlaceholder?: string;
  noResultsMsg?: string;
  selectItemMsg?: string;
  className?: string;
  unselect?: boolean;
  unselectMsg?: string;
  onSearchChange?: (e: string) => void;
  popoverContentClassName?: string;
  disabled?: boolean;
  handleFilter?: (value: string, search: string) => number;
  testTag?: string;
  drawerTitle?: string;
};

export function OnboardingCombobox({
  value,
  onSelect,
  items,
  searchPlaceholder,
  noResultsMsg,
  selectItemMsg,
  className,
  unselect = false,
  unselectMsg,
  onSearchChange,
  popoverContentClassName,
  disabled,
  handleFilter,
  testTag,
  drawerTitle,
}: OnboardingComboboxProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const resolvedSearchPlaceholder =
    searchPlaceholder ??
    intl.formatMessage(personalizationSharedMessages.comboboxSearchPlaceholder);
  const resolvedNoResultsMsg =
    noResultsMsg ?? intl.formatMessage(personalizationSharedMessages.noResultsFound);
  const resolvedSelectItemMsg =
    selectItemMsg ?? intl.formatMessage(personalizationSharedMessages.comboboxSelectItem);
  const resolvedUnselectMsg =
    unselectMsg ?? intl.formatMessage(personalizationSharedMessages.comboboxUnselectItem);
  const heidiProAlt = intl.formatMessage(personalizationSharedMessages.heidiProAlt);

  const handleOnSearchChange = useDebouncedCallback((e: string) => {
    if (e === '') {
      return;
    }

    if (onSearchChange) {
      onSearchChange(e);
    }
  }, 300);

  const { isFreeUser } = useIsFreeUser();

  const selectedItem = value ? items.find((item) => item.value === value) : undefined;

  return (
    <PopoverDrawer open={open} onOpenChange={setOpen}>
      <PopoverDrawerTrigger asChild>
        <ComboboxTrigger
          open={open}
          disabled={disabled}
          isPlaceholder={!selectedItem}
          role="combobox"
          className={className}
          data-testid={testTag}
        >
          {/* flex-1 + justify-between so the Pro badge sits beside the trigger's
              own chevron rather than being spread to the middle of the row. */}
          <span className="flex min-w-0 flex-1 items-center justify-between gap-x-2">
            <TypographyP2 className="truncate">
              {selectedItem?.label ?? resolvedSelectItemMsg}
            </TypographyP2>
            {selectedItem?.isPro && isFreeUser && (
              <HeidiProFlashFilledSvg alt={heidiProAlt} className="size-4 shrink-0" />
            )}
          </span>
        </ComboboxTrigger>
      </PopoverDrawerTrigger>
      <PopoverDrawerContent
        className={cn(
          isMobile ? 'px-0 pb-4' : 'w-[var(--radix-popover-trigger-width)] p-0',
          popoverContentClassName
        )}
      >
        {isMobile && drawerTitle && (
          <DrawerHeader>
            <DrawerTitle>{drawerTitle}</DrawerTitle>
          </DrawerHeader>
        )}
        <Command filter={handleFilter}>
          <CommandInput
            placeholder={resolvedSearchPlaceholder}
            onValueChange={handleOnSearchChange}
          />
          <ScrollArea
            className={
              isMobile ? 'max-h-[60vh] overflow-auto' : 'max-h-[220px] overflow-auto'
            }
          >
            <CommandEmpty>{resolvedNoResultsMsg}</CommandEmpty>
            <CommandGroup>
              {unselect && (
                <CommandItem
                  key="unselect"
                  value=""
                  onSelect={() => {
                    onSelect('');
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      value === '' ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {resolvedUnselectMsg}
                </CommandItem>
              )}
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  onSelect={(currentValue) => {
                    onSelect(currentValue === item.value ? item.value : '');
                    setOpen(false);
                  }}
                  data-testid={item.testTag}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      value === item.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {item.label}
                  {item.isPro && isFreeUser && (
                    <HeidiProFlashFilledSvg
                      alt={heidiProAlt}
                      className="ml-auto size-4"
                    />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </ScrollArea>
        </Command>
      </PopoverDrawerContent>
    </PopoverDrawer>
  );
}
